/* Feb-2026 · Grand Opening swag signup page — first-500 email capture.
 *
 * Route:  /bookstore/swag
 * Backend:  POST /api/bookstore/swag/signup, GET /api/bookstore/swag/stats
 *
 * We collect the minimum viable data (email + first name + optional
 * country + optional shipping hint) so the page converts. Full
 * shipping address is captured after launch via a follow-up email.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Gift, Sparkles, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax  = axios.create({ baseURL: `${API}/api` });

export default function SwagSignupPage() {
  const [stats, setStats]       = useState(null);
  const [form, setForm]         = useState({
    email: '', first_name: '', last_name: '',
    country_code: '', shipping_hint: '',
  });
  const [submitting, setSub]    = useState(false);
  const [success, setSuccess]   = useState(null);

  useEffect(() => {
    ax.get('/bookstore/swag/stats')
      .then((r) => setStats(r.data))
      .catch(() => setStats({ count: 0, cap: 500, remaining: 500,
                              percent_full: 0, waitlist: false }));
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) {
      toast.error('Email and first name are required.');
      return;
    }
    setSub(true);
    try {
      const r = await ax.post('/bookstore/swag/signup',
                                 { ...form, source: 'swag-page' });
      setSuccess(r.data);
      // Refresh stats so the progress bar animates forward.
      const s = await ax.get('/bookstore/swag/stats');
      setStats(s.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed. Please retry.');
    } finally { setSub(false); }
  };

  const pctFull  = stats?.percent_full ?? 0;
  const remaining = stats?.remaining  ?? 500;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#2d0f18] via-[#3b0f1a] to-[#1a0810] text-white"
      data-testid="swag-signup-page"
    >
      <div className="mx-auto max-w-2xl px-6 py-10">
        <Link
          to="/bookstore"
          className="mb-6 inline-flex items-center gap-1 text-[12px] text-white/70 transition hover:text-white"
          data-testid="swag-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to the bookstore
        </Link>

        <div className="mb-8 flex items-center gap-3">
          <div className="rounded-full bg-white/10 p-3">
            <Gift className="h-8 w-8" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/60">
              Crown &amp; Rose · Dec 15, 2026
            </div>
            <h1 className="font-serif text-3xl md:text-4xl">Claim your grand-opening swag.</h1>
          </div>
        </div>

        <p className="mb-6 text-[15px] leading-relaxed text-white/85">
          The first <b>500 customers</b> to sign up before December 15, 2026
          get a free Crown &amp; Rose swag pack — a signed indie novel from
          our launch shelf, a matching bookmark, and a foil-stamped launch
          sticker set. Drop your email and we&#39;ll email the shipping
          form the day the store opens.
        </p>

        {stats && (
          <div className="mb-6 rounded-md border border-white/15 bg-white/5 p-4" data-testid="swag-progress-card">
            <div className="mb-2 flex items-center justify-between text-[12px] uppercase tracking-wider">
              <span className="text-white/70">
                <span className="font-semibold text-white">{stats.count}</span> claimed
              </span>
              <span className="text-white/70">
                {stats.waitlist ? 'Waitlist open' : `${remaining} spots left`}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-gradient-to-r from-amber-300 via-rose-400 to-rose-500 transition-all duration-700"
                style={{ width: `${Math.max(1.5, pctFull)}%` }}
                data-testid="swag-progress-bar"
              />
            </div>
            {stats.waitlist && (
              <p className="mt-2 text-[11.5px] text-amber-200">
                We've hit the 500-pack cap — new signups land on the waitlist
                for the second wave.
              </p>
            )}
          </div>
        )}

        {success ? (
          <div
            className="rounded-md border border-emerald-300/40 bg-emerald-500/10 p-5"
            data-testid="swag-success-card"
          >
            <CheckCircle2 className="mb-2 h-6 w-6 text-emerald-300" />
            <h2 className="mb-1 font-serif text-xl">
              You&#39;re signed up, {form.first_name}!
            </h2>
            <p className="text-[13.5px] text-white/85">
              You&#39;re {success.waitlist
                ? <>on the <b>waitlist</b> (second-wave shipments start January).</>
                : <>#<b>{success.rank}</b> of 500 — swag is yours.</>}
            </p>
            <p className="mt-2 text-[12px] text-white/70">
              We&#39;ll email <b>{form.email}</b> on Dec 15 to confirm your
              shipping address. In the meantime — go check out the Bookstore.
            </p>
            <div className="mt-4 flex gap-2">
              <Link to="/bookstore"
                    className="inline-flex items-center gap-1 rounded-full bg-white/15 px-4 py-2 text-[12px] font-semibold uppercase tracking-wider transition hover:bg-white/25"
                    data-testid="swag-cta-bookstore">
                <Sparkles className="h-3.5 w-3.5" /> Explore the bookstore
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3"
                data-testid="swag-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-white/70">First name *</label>
                <Input value={form.first_name} onChange={onChange('first_name')}
                        placeholder="Alex" required
                        data-testid="swag-first-name-input"
                        className="mt-1 h-9 border-white/20 bg-white/5 text-white placeholder:text-white/40" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-white/70">Last name</label>
                <Input value={form.last_name} onChange={onChange('last_name')}
                        placeholder="(optional)"
                        data-testid="swag-last-name-input"
                        className="mt-1 h-9 border-white/20 bg-white/5 text-white placeholder:text-white/40" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/70">Email *</label>
              <Input type="email" value={form.email} onChange={onChange('email')}
                      placeholder="alex@example.com" required
                      data-testid="swag-email-input"
                      className="mt-1 h-9 border-white/20 bg-white/5 text-white placeholder:text-white/40" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/70">Country</label>
              <Input value={form.country_code} onChange={onChange('country_code')}
                      placeholder="US, CA, UK…" maxLength={3}
                      data-testid="swag-country-input"
                      className="mt-1 h-9 border-white/20 bg-white/5 text-white placeholder:text-white/40" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/70">
                Shipping hint (optional)
              </label>
              <Textarea value={form.shipping_hint} onChange={onChange('shipping_hint')}
                          placeholder="City, state / zip — full address collected after launch"
                          rows={2}
                          data-testid="swag-shipping-hint-input"
                          className="mt-1 border-white/20 bg-white/5 text-white placeholder:text-white/40" />
            </div>

            <Button type="submit" disabled={submitting}
                      className="mt-2 w-full bg-white text-[#3b0f1a] hover:bg-white/90"
                      data-testid="swag-submit-btn">
              {submitting
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Gift className="mr-2 h-4 w-4" />}
              Claim my swag pack
            </Button>
            <p className="pt-1 text-center text-[10.5px] text-white/50">
              No spam. One email on Dec 15 with the shipping form, then
              you can unsubscribe.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
